import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { useTypingIndicator } from '../hooks/useTypingIndicator';
import { useGlobalLoading } from '../contexts/LoadingContext';
import { type Conversation as ApiConversation, type User as UserType } from '../services/api';
import type { MinimalDocument } from '../types';
import type {
  DocumentSelectionMode,
  ResponseStyle,
  ResponseLength,
} from '../types/documentSelection';
import type { ChatMessage } from './ChatPanel';
import logger from '../utils/simpleLogger';
import { eventBus, EventNames, useEventListener } from '../utils/eventBus';
import { apiService } from '../services/api';
import { useDocumentSelection } from '../hooks/useDocumentSelection';
import {
  useStreamingManager,
  useDocumentSync,
  useMentionManager,
  useSendMessage,
} from './chat/hooks';
import { useDocumentSuggestions } from './chat/hooks/useDocumentSuggestions';
import {
  MessageStatusBar,
  InputToolbar,
  InputContainer,
  useInputHandlers,
  useClearContext,
  useSuggestedQuestions,
} from './chat/input';
import SuggestionIndicator from './chat/document-panel/SuggestionIndicator';

interface ChatInputAreaProps {
  conversation: ApiConversation | null;
  selectedDocuments: string[];
  selectedMinimalDocuments?: MinimalDocument[];
  allDocumentIds?: string[];
  onDocumentSelect?: (documentIds: string[]) => void;
  messages: ChatMessage[];
  onMessagesUpdate: React.Dispatch<React.SetStateAction<ChatMessage[]>>;
  onConversationCleared?: () => Promise<void> | void;
  onDocumentUpload?: (file: File) => void;
  onDocumentUploadShared?: (file: File) => void;
  currentUser?: UserType;
  onSuccessMessageChange?: (message: string | null) => void;
  onConversationCreate?: (name: string) => Promise<any>;
  onNewConversation?: () => void;
}

const ChatInputArea: React.FC<ChatInputAreaProps> = ({
  conversation,
  selectedDocuments,
  selectedMinimalDocuments = [],
  allDocumentIds = [],
  onDocumentSelect,
  messages,
  onMessagesUpdate,
  onConversationCleared,
  onDocumentUpload,
  onDocumentUploadShared,
  currentUser,
  onSuccessMessageChange,
  onConversationCreate,
  onNewConversation,
}) => {
  const { theme } = useTheme();
  const { isTyping, startTyping: _startTyping, stopTyping: _stopTyping } = useTypingIndicator();
  // Bridge typing state to ChatPanel via EventBus (they live in separate React trees)
  const startTyping = useCallback(() => {
    _startTyping();
    eventBus.emit(EventNames.TYPING_STATE_CHANGED, { isTyping: true });
  }, [_startTyping]);
  const stopTyping = useCallback(() => {
    _stopTyping();
    eventBus.emit(EventNames.TYPING_STATE_CHANGED, { isTyping: false });
  }, [_stopTyping]);
  const { startLoading, stopLoading } = useGlobalLoading();
  const [inputMessage, setInputMessage] = useState('');
  const [error, setError] = useState<string | null>(null);
  // Low confidence banner dismissed state
  const [lowConfidenceDismissed, setLowConfidenceDismissed] = useState(false);
  // C3: Streaming timeout state
  const [isStreamingTimeout, setIsStreamingTimeout] = useState(false);
  // C5: Last sent message for retry
  const lastSentMessageRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputSharedRef = useRef<HTMLInputElement>(null);

  // Document selection mode: loaded via EventBus from useChatSettings (single source of truth)
  const [documentSelectionMode, setDocumentSelectionMode] =
    useState<DocumentSelectionMode>('smart');

  // Response style: loaded from AI settings on mount
  const [responseStyle, setResponseStyle] = useState<ResponseStyle>('normal');

  // Response length: loaded from AI settings on mount
  const [responseLength, setResponseLength] = useState<ResponseLength>('auto');

  // Model and temperature: loaded from AI settings on mount
  const [currentModel, setCurrentModel] = useState<string>('gpt-4o-mini');
  const [currentTemperature, setCurrentTemperature] = useState<number>(0.0);
  const [availableModels, setAvailableModels] = useState<
    Array<{ id: string; name: string; recommended?: boolean }>
  >([]);

  // Suggested content toggles: loaded from AI settings on mount
  const [suggestedQuestionsEnabled, setSuggestedQuestionsEnabled] = useState(true);
  const [suggestedTasksEnabled, setSuggestedTasksEnabled] = useState(true);

  // Listen for mode changes from useChatSettings/ControlPanel via event bus
  // D04 fix: use useEventListener for stable ref, prevents listener accumulation
  useEventListener('doc_selection_mode_changed', (data: { mode: DocumentSelectionMode }) => {
    setDocumentSelectionMode(data.mode);
    clearSuggestions();
  });

  // Listen for response style changes from ControlPanel via event bus
  useEventListener('response_style_changed', (data: { style: ResponseStyle }) => {
    setResponseStyle(data.style);
  });

  // Listen for response length changes from ControlPanel via event bus
  useEventListener('response_length_changed', (data: { length: ResponseLength }) => {
    setResponseLength(data.length);
  });

  // Listen for suggested content toggle changes from ControlPanel via event bus
  useEventListener(EventNames.SUGGESTED_QUESTIONS_TOGGLED, (data: { enabled: boolean }) => {
    setSuggestedQuestionsEnabled(data.enabled);
  });
  useEventListener(EventNames.SUGGESTED_TASKS_TOGGLED, (data: { enabled: boolean }) => {
    setSuggestedTasksEnabled(data.enabled);
  });

  // Load AI settings (style, length, model, temperature) on mount
  useEffect(() => {
    const loadAISettings = async () => {
      try {
        const response = await apiService.getAISettings();
        if (response.success && response.settings?.settings) {
          const s = response.settings.settings;
          const style = s.response_style;
          if (style && ['normal', 'concise', 'detailed', 'technical', 'simple'].includes(style)) {
            setResponseStyle(style as ResponseStyle);
          }
          const length = s.response_length;
          if (length && ['auto', 'short', 'long'].includes(length)) {
            setResponseLength(length as ResponseLength);
          }
          if (s.model) {
            setCurrentModel(s.model);
          }
          if (s.temperature !== undefined) {
            setCurrentTemperature(s.temperature);
          }
          if (s.suggested_questions_enabled !== undefined) {
            setSuggestedQuestionsEnabled(s.suggested_questions_enabled);
          }
          if (s.suggested_tasks_enabled !== undefined) {
            setSuggestedTasksEnabled(s.suggested_tasks_enabled);
          }
        }
      } catch (err) {
        console.error('Error loading AI settings:', err);
      }
    };
    loadAISettings();
  }, []);

  // Load available models list on mount
  useEffect(() => {
    const loadModels = async () => {
      try {
        const config = await apiService.getAIConfig();
        if (config.success && config.available_models) {
          setAvailableModels(config.available_models);
        }
      } catch (err) {
        console.error('Error loading available models:', err);
      }
    };
    loadModels();
  }, []);

  const {
    selectedIds: contextSelectedIds,
    filters: contextFilters,
    selectedDocuments: contextSelectedDocs,
    selectDocuments: selectContextDocuments,
    deselectDocuments: deselectContextDocuments,
    setFilters: setContextFilters,
    clearSelection: clearContextSelection,
    setDocument,
    loadDocument,
    aiSuggestedIds,
  } = useDocumentSelection();

  const setSuccessMessageWithNotification = useCallback(
    (message: string | null) => onSuccessMessageChange?.(message),
    [onSuccessMessageChange]
  );

  const {
    streamAbortControllerRef,
    streamingMessageIdRef,
    cleanupStreamingRefs,
    handleStreamCancellation: handleStreamCancellationBase,
    startAbsoluteTimeout,
  } = useStreamingManager({
    onMessagesUpdate,
    setError,
    setSuccessMessage: setSuccessMessageWithNotification,
    stopTyping,
    stopLoading,
  });

  const handleStreamCancellation = useCallback(() => {
    handleStreamCancellationBase();
  }, [handleStreamCancellationBase]);

  // Cleanup on unmount: ensure typing indicator is cleared so ChatPanel doesn't get stuck
  useEffect(() => {
    return () => {
      eventBus.emit(EventNames.TYPING_STATE_CHANGED, { isTyping: false });
    };
  }, []);

  const { handleDocumentsAutoSelect } = useDocumentSync({
    selectedMinimalDocuments,
    contextSelectedIds,
    selectContextDocuments,
    deselectContextDocuments,
    clearContextSelection,
    loadDocument,
    setDocument,
    onDocumentSelect,
    inputMessage,
    setInputMessage,
  });

  const {
    mentionAutocompleteRef,
    formattedInputRef,
    handleCompanySelect,
    handleInsertAt,
    handleMentionClick,
  } = useMentionManager({
    inputMessage,
    setInputMessage,
    contextFilters,
    setContextFilters,
    setSuccessMessage: setSuccessMessageWithNotification,
    selectedMinimalDocuments,
    contextSelectedDocs,
    contextSelectedIds,
    deselectContextDocuments,
    onDocumentSelect,
  });

  // Smart mode document suggestions
  const {
    suggestions,
    isLoading: isSuggestionsLoading,
    clearSuggestions,
    setStreamSuggestions,
    confidence: suggestionsConfidence,
    userPromptNeeded,
  } = useDocumentSuggestions({
    conversationId: conversation?.id,
    mode: documentSelectionMode,
    inputText: inputMessage,
  });

  // Broadcast suggestions to DocumentContextSelector (lives in MainLayout) via EventBus
  useEffect(() => {
    eventBus.emit(EventNames.DOCUMENT_SUGGESTIONS_UPDATED, {
      suggestions,
      isLoading: isSuggestionsLoading,
      userPromptNeeded,
    });
  }, [suggestions, isSuggestionsLoading, userPromptNeeded]);

  // Pre-select suggested documents in context when suggestions arrive.
  // Clear previous AI suggestions first to prevent unbounded accumulation across turns.
  const prevSuggestionIdsRef = useRef<string>('');
  useEffect(() => {
    const newIds = suggestions
      .map((s) => s.id)
      .sort()
      .join(',');
    if (newIds && newIds !== prevSuggestionIdsRef.current) {
      prevSuggestionIdsRef.current = newIds;
      setLowConfidenceDismissed(false); // Reset banner for new suggestions
      const ids = suggestions.map((s) => s.id);

      // Remove previous AI-suggested docs that aren't in the new set
      const newIdSet = new Set(ids);
      const staleAiIds = [...aiSuggestedIds].filter((id) => !newIdSet.has(id));
      if (staleAiIds.length > 0) {
        deselectContextDocuments(staleAiIds);
      }

      selectContextDocuments(ids, 'ai-system');

      // Auto-switch sidebar to "Seleccionados" when docs are auto-selected
      eventBus.emit(EventNames.OPEN_DOCUMENT_PANEL, { tab: 'seleccionados', onlyIfOpen: true });

      // Populate document objects in context so SelectedDocsTab can display them
      suggestions.forEach((s) => {
        setDocument({
          id: s.id,
          name: s.name,
          documentType: 'other',
          scope: 'shared',
        });
      });
    }
  }, [suggestions, selectContextDocuments, deselectContextDocuments, aiSuggestedIds, setDocument]);

  // Clear document selection when switching between existing conversations,
  // but NOT when creating the first conversation (null → new-id).
  // The null→new transition happens inside sendMessage when ensureConversation
  // auto-creates a conversation, and clearing docs at that point would wipe
  // the user's manual selection mid-send.
  const prevConvIdRef = useRef(conversation?.id);
  useEffect(() => {
    if (conversation?.id !== prevConvIdRef.current) {
      const wasRealConversation = prevConvIdRef.current != null;
      prevConvIdRef.current = conversation?.id;
      if (wasRealConversation) {
        clearContextSelection();
        prevSuggestionIdsRef.current = '';
      }
    }
  }, [conversation?.id, clearContextSelection]);

  const handleRemoveSuggestion = useCallback(
    (id: string) => {
      deselectContextDocuments([id]);
    },
    [deselectContextDocuments]
  );

  const { sendMessage: sendMessageHook } = useSendMessage({
    conversation,
    selectedDocuments,
    onMessagesUpdate,
    onConversationCreate,
    streamAbortControllerRef,
    streamingMessageIdRef,
    cleanupStreamingRefs,
    startTyping,
    stopTyping,
    startLoading,
    stopLoading,
    setError,
    handleStreamCancellation,
    startAbsoluteTimeout,
    onStreamingEnd: () => formattedInputRef.current?.focus(),
    onStreamingTimeout: setIsStreamingTimeout,
    onSuggestion: (data) => {
      // Fallback: when pre-select can't fire (e.g. new conversation with no conversationId),
      // use the auto-selected documents from the SSE stream to populate the suggestions panel.
      const docs = data.suggested_documents;
      if (docs?.length && documentSelectionMode === 'smart') {
        setStreamSuggestions(
          docs.map((d: any) => ({
            id: d.id,
            name: d.name ?? d.id,
            reason: d.reason,
            strategy: d.strategy,
            score: d.score ?? d.relevance,
          }))
        );
      }
    },
  });

  const sendMessage = useCallback(
    async (
      messageTextOverride?: string,
      options?: { studio_id?: string; fallback_document_ids?: string[] }
    ) => {
      setIsStreamingTimeout(false);
      // C5: Store last sent message for retry
      lastSentMessageRef.current = messageTextOverride || inputMessage;

      // When sending with no selected docs, extract doc IDs from the last AI
      // message's citations (covers auto-selected docs that were cleaned up
      // after the previous send — applies to both studio tasks and follow-up questions).
      let enrichedOptions = options;
      if (selectedDocuments.length === 0 && messages.length > 0) {
        const lastAiMsg = [...messages].reverse().find((m) => !m.isUser && m.citations);
        if (lastAiMsg?.citations) {
          const citationDocIds = [
            ...new Set(
              Object.values(lastAiMsg.citations)
                .map((c) => c.document_id)
                .filter(Boolean) as string[]
            ),
          ];
          if (citationDocIds.length > 0) {
            enrichedOptions = { ...options, fallback_document_ids: citationDocIds };
          }
        }
      }

      await sendMessageHook(
        { inputMessage, contextFilters },
        setInputMessage,
        messageTextOverride,
        enrichedOptions
      );
      // Suggestions persist in panel after send — they show which docs the AI used.
      // Pre-select useEffect handles dedup/replacement when new suggestions arrive.
      // fallback_document_ids (lines 340-358) covers follow-ups when docs are cleared.
    },
    [
      sendMessageHook,
      inputMessage,
      contextFilters,
      documentSelectionMode,
      selectedDocuments,
      messages,
    ]
  );

  // C5: Retry last failed message
  const handleRetry = useCallback(() => {
    if (lastSentMessageRef.current) {
      sendMessage(lastSentMessageRef.current);
    }
  }, [sendMessage]);

  const { handleKeyDown, handleDocumentChipClick, canSend } = useInputHandlers({
    inputMessage,
    selectedMinimalDocuments,
    selectedDocuments,
    contextFilters,
    mentionAutocompleteRef,
    deselectContextDocuments,
    setContextFilters,
    sendMessage,
  });

  const { handleClearContext } = useClearContext({
    conversation,
    messages,
    onMessagesUpdate,
    onConversationCleared,
    setError,
    setSuccessMessage: setSuccessMessageWithNotification,
  });

  const handleCancelStreaming = useCallback(() => {
    streamAbortControllerRef.current?.abort();
    handleStreamCancellation();
  }, [streamAbortControllerRef, handleStreamCancellation]);

  useSuggestedQuestions({ sendMessage, setInputMessage, formattedInputRef });

  const handleToggleControlPanel = useCallback(() => {
    eventBus.emit(EventNames.TOGGLE_CONTROL_PANEL);
  }, []);

  // Inline mode change handler: persist to backend + notify other components
  const handleModeChange = useCallback(
    async (mode: DocumentSelectionMode) => {
      setDocumentSelectionMode(mode);
      clearSuggestions();
      eventBus.emit('doc_selection_mode_changed', { mode });
      try {
        await apiService.updateAISettings({ document_selection_mode: mode });
      } catch (err) {
        console.error('Error updating document selection mode:', err);
      }
    },
    [clearSuggestions]
  );

  // Inline style change handler: persist to backend + notify other components
  const handleStyleChange = useCallback(async (style: ResponseStyle) => {
    setResponseStyle(style);
    eventBus.emit('response_style_changed', { style });
    try {
      await apiService.updateAISettings({ response_style: style });
    } catch (err) {
      console.error('Error updating response style:', err);
    }
  }, []);

  // Inline length change handler: persist to backend + notify other components
  const handleLengthChange = useCallback(async (length: ResponseLength) => {
    setResponseLength(length);
    eventBus.emit('response_length_changed', { length });
    try {
      await apiService.updateAISettings({ response_length: length });
    } catch (err) {
      console.error('Error updating response length:', err);
    }
  }, []);

  // Inline model change handler: persist to backend
  const handleModelChange = useCallback(async (modelId: string) => {
    setCurrentModel(modelId);
    try {
      await apiService.updateAISettings({ model: modelId });
    } catch (err) {
      console.error('Error updating model:', err);
    }
  }, []);

  // Inline temperature change handler: persist to backend
  const handleTemperatureChange = useCallback(async (temp: number) => {
    setCurrentTemperature(temp);
    try {
      await apiService.updateAISettings({ temperature: temp });
    } catch (err) {
      console.error('Error updating temperature:', err);
    }
  }, []);

  const handleSuggestedQuestionsToggle = useCallback(async (enabled: boolean) => {
    setSuggestedQuestionsEnabled(enabled);
    eventBus.emit(EventNames.SUGGESTED_QUESTIONS_TOGGLED, { enabled });
    try {
      await apiService.updateAISettings({ suggested_questions_enabled: enabled });
    } catch (err) {
      console.error('Error updating suggested questions:', err);
      setSuggestedQuestionsEnabled(!enabled);
    }
  }, []);

  const handleSuggestedTasksToggle = useCallback(async (enabled: boolean) => {
    setSuggestedTasksEnabled(enabled);
    eventBus.emit(EventNames.SUGGESTED_TASKS_TOGGLED, { enabled });
    try {
      await apiService.updateAISettings({ suggested_tasks_enabled: enabled });
    } catch (err) {
      console.error('Error updating suggested tasks:', err);
      setSuggestedTasksEnabled(!enabled);
    }
  }, []);

  /**
   * Copy entire conversation to clipboard in plain text format
   */
  const handleCopyConversation = useCallback(() => {
    if (!messages || messages.length === 0) return;

    const validMessages = messages.filter((m) => !m.isStreaming && !m.variant);
    if (validMessages.length === 0) return;

    // Header with conversation name, date, and message count
    const convName = conversation?.name || 'Conversación';
    const date = new Date().toLocaleDateString('es-CL', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
    const header = `${convName}\n${date} · ${validMessages.length} mensajes\n`;

    // Format each message as plain text — sender and content on same line
    const body = validMessages
      .map((m) => {
        const sender = m.isUser ? 'Tú' : 'Asistente';
        let content = m.text || m.markdown || '';
        if (!m.isUser) {
          content = content.replace(/\[(\d+)\]/g, '');
        }
        return `${sender}: ${content}`;
      })
      .join('\n\n');

    navigator.clipboard.writeText(`${header}\n${body}\n`);
    onSuccessMessageChange?.(`Conversación copiada (${validMessages.length} mensajes)`);
  }, [messages, conversation, onSuccessMessageChange]);

  const containerStyle = {
    paddingLeft: 'clamp(1rem, 4vw, 2.5rem)',
    paddingRight: 'clamp(1rem, 4vw, 2.5rem)',
    boxSizing: 'border-box' as const,
  };

  return (
    <>
      <div className="flex-shrink-0 py-4 w-full" style={containerStyle}>
        <div className="mx-auto space-y-3" style={{ width: '100%', maxWidth: '95%' }}>
          <MessageStatusBar
            error={error}
            successMessage={null}
            isStreaming={isTyping}
            onCancelStreaming={handleCancelStreaming}
            onDismissError={() => setError(null)}
            onDismissSuccess={() => {}}
            onRetry={error ? handleRetry : undefined}
            isStreamingTimeout={isStreamingTimeout}
          />
          <InputContainer
            inputMessage={inputMessage}
            onInputChange={setInputMessage}
            onKeyDown={handleKeyDown}
            onMentionClick={handleMentionClick}
            onDocumentChipClick={handleDocumentChipClick}
            onMentionSelect={handleCompanySelect}
            onFiltersChange={setContextFilters}
            onDocumentsAutoSelect={handleDocumentsAutoSelect}
            contextFilters={contextFilters}
            selectedDocumentIds={contextSelectedIds}
            conversationId={conversation?.id}
            isStreaming={isTyping}
            canSend={canSend && !isTyping}
            onSend={sendMessage}
            onCancel={handleCancelStreaming}
            formattedInputRef={formattedInputRef}
            mentionAutocompleteRef={mentionAutocompleteRef}
          >
            <InputToolbar
              selectedDocumentCount={selectedDocuments.length}
              totalDocumentCount={allDocumentIds.length}
              isStreaming={isTyping}
              disabled={false}
              onDocumentUpload={onDocumentUpload}
              onDocumentUploadShared={onDocumentUploadShared}
              fileInputRef={fileInputRef}
              fileInputSharedRef={fileInputSharedRef}
              isAdmin={currentUser?.is_admin || false}
              documentSelectionMode={documentSelectionMode}
              onModeChange={handleModeChange}
              responseStyle={responseStyle}
              onStyleChange={handleStyleChange}
              responseLength={responseLength}
              onLengthChange={handleLengthChange}
              currentModel={currentModel}
              availableModels={availableModels}
              onModelChange={handleModelChange}
              currentTemperature={currentTemperature}
              onTemperatureChange={handleTemperatureChange}
              suggestedQuestionsEnabled={suggestedQuestionsEnabled}
              suggestedTasksEnabled={suggestedTasksEnabled}
              onSuggestedQuestionsToggle={handleSuggestedQuestionsToggle}
              onSuggestedTasksToggle={handleSuggestedTasksToggle}
            >
              {/* SuggestionIndicator: inline badge in toolbar (Smart mode only) */}
              {documentSelectionMode === 'smart' && (
                <SuggestionIndicator
                  count={suggestions.length}
                  isLoading={isSuggestionsLoading}
                  disabled={isTyping}
                />
              )}
            </InputToolbar>
          </InputContainer>
          <p className={`text-center text-[10px] ${theme.colors.text.secondary} opacity-50`}>
            MyChat usa IA y puede cometer errores.
          </p>
        </div>
      </div>
    </>
  );
};

export default ChatInputArea;
